import { Status, StatusType } from '@app/components/various/Status';
import { getIsInvoiceFull } from '@app/logic/invoices';
import { isDefined } from '@app/utils/is';
import { Invoice } from '@typings';
import { useTranslation } from 'react-i18next';

import { InvoiceHeader } from '../InvoiceHeader';
import { InvoiceStatusLabel } from '../InvoiceStatusLabel';

import { InvoiceInfoItem } from './InvoiceInfoItem';

interface Props {
  invoiceIdentifiers: Invoice.InvoiceIdentifiers | null;
  invoice: Invoice.Full | Invoice.Base | undefined;
  invoiceStatus: StatusType | undefined;
  isLoading: boolean;
}

export const InvoiceInfo = ({ invoice, invoiceIdentifiers, invoiceStatus, isLoading }: Props) => {
  const { t } = useTranslation(['common', 'invoices']);

  return (
    <section className="mb-16 mt-8">
      <InvoiceHeader invoice={invoice} invoiceIdentifiers={invoiceIdentifiers} isLoading={isLoading} />

      <dl className="mt-8 grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-3 sm:grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] lg:mt-5">
        <InvoiceInfoItem
          isLoading={isLoading}
          name={t('common:status')}
          value={
            !isDefined(invoiceStatus) ? undefined : (
              <div className="flex flex-nowrap items-center gap-2">
                <Status type={invoiceStatus} />
                <InvoiceStatusLabel status={invoiceStatus} />
              </div>
            )
          }
        />
        <InvoiceInfoItem isLoading={isLoading} dataTestId="invoice-created-at" name={t('common:created')} value={invoice?.createdAt} />
        <InvoiceInfoItem isLoading={isLoading} dataTestId="invoice-due-date" name={t('common:due_date')} value={invoice?.dueDate} />
        {isDefined(invoice) && getIsInvoiceFull(invoice) && (
          <>
            <InvoiceInfoItem
              dataTestId="invoice-company"
              name={t('common:company')}
              value={<span data-sentry-mask>{invoice.company}</span>}
            />
            <InvoiceInfoItem
              dataTestId="invoice-address"
              name={t('common:address')}
              value={invoice.address.split(',').map(address => (
                <div data-sentry-mask key={address}>
                  {address.trim()}
                </div>
              ))}
            />
            <InvoiceInfoItem dataTestId="invoice-shipment-number" name={t('common:shipment_one')} value={invoice.shipmentId} />
            <InvoiceInfoItem dataTestId="invoice-payment-terms" name={t('invoices:payment_terms')} value={invoice.paymentTerms} />
            <InvoiceInfoItem dataTestId="invoice-shipping-terms" name={t('invoices:shipping_terms')} value={invoice.shippingTerms} />
          </>
        )}
      </dl>
    </section>
  );
};
